// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	encodePos,
	ROOM_LIMITS,
	ROOM_POS_REQUEST_TOPIC,
	ROOM_POS_TOPIC,
	type RoomPose,
} from '@fluxer/constants/src/RoomCharacters';
import {type RemoteParticipant, type Room, RoomEvent} from 'livekit-client';
import {useEffect} from 'react';

// Where my character walks: null while it sits (or walking is not on). Read on every send tick.
export interface RoomPoseSource {
	getPose(): RoomPose | null;
}

const SEND_INTERVAL_MS = 1000 / ROOM_LIMITS.posSendHz;
// Half a tick early counts: a hidden tab's once-a-second wake-ups, a few ms off, would push the heartbeat to 3 s.
const HEARTBEAT_MS = ROOM_LIMITS.posIdleHeartbeatS * 1000 - SEND_INTERVAL_MS / 2;
const REPLY_DELAY_MAX_MS = 300;
const REPLY_COOLDOWN_MS = 5000;

const samePacket = (a: Uint8Array, b: Uint8Array): boolean => a.every((byte, i) => byte === b[i]);

// Sends where my character walks, lossy: every change at up to 10 a second; standing still, the same packet every 2 s
// for as long as it stands (the window out of focus too); an end packet when it sits down or the room passes the
// walking cap; then nothing. A newcomer's request is answered with my last packet, to them only, 0–300 ms later and
// at most once per 5 s each. The position goes only into the packet: never logged, stored or sent to the API.
export function usePositionSender({source, room}: {source: RoomPoseSource | null; room: Room | null}): void {
	useEffect(() => {
		if (!source || !room) return;
		let last: {pose: RoomPose; packet: Uint8Array<ArrayBuffer>} | null = null;
		let active = false;
		let sentAt = Number.NEGATIVE_INFINITY;
		const publish = (packet: Uint8Array<ArrayBuffer>, destinationIdentities?: Array<string>): void => {
			// Lossy by design: a lost packet is replaced by the next one.
			room.localParticipant
				.publishData(packet, {
					reliable: false,
					topic: ROOM_POS_TOPIC,
					...(destinationIdentities ? {destinationIdentities} : {}),
				})
				.catch(() => {});
		};
		const end = (): void => {
			if (active && last) publish(encodePos(last.pose, true));
			active = false;
		};
		const tick = (): void => {
			const now = performance.now();
			const pose = room.remoteParticipants.size + 1 <= ROOM_LIMITS.posMaxParticipants ? source.getPose() : null;
			if (!pose) {
				end();
				last = null;
				return;
			}
			const packet = encodePos(pose);
			if (!last || !samePacket(packet, last.packet)) {
				last = {pose, packet};
				active = true;
			} else if (now - sentAt < HEARTBEAT_MS) {
				return;
			}
			sentAt = now;
			publish(packet);
		};

		const pending = new Set<string>();
		const replied = new Map<string, number>();
		let replyTimer: ReturnType<typeof setTimeout> | null = null;
		const reply = (): void => {
			replyTimer = null;
			if (active && last) publish(last.packet, [...pending]);
			pending.clear();
		};
		const onData = (payload: Uint8Array, participant?: RemoteParticipant, _kind?: unknown, topic?: string): void => {
			if (topic !== ROOM_POS_REQUEST_TOPIC || !participant || payload.byteLength !== 0 || !active) return;
			const now = performance.now();
			const {identity} = participant;
			if (now - (replied.get(identity) ?? Number.NEGATIVE_INFINITY) < REPLY_COOLDOWN_MS) return;
			replied.set(identity, now);
			pending.add(identity);
			replyTimer ??= setTimeout(reply, Math.random() * REPLY_DELAY_MAX_MS);
		};
		const onLeft = (participant: RemoteParticipant): void => {
			replied.delete(participant.identity);
			pending.delete(participant.identity);
		};

		const interval = setInterval(tick, SEND_INTERVAL_MS);
		room.on(RoomEvent.DataReceived, onData);
		room.on(RoomEvent.ParticipantDisconnected, onLeft);
		return () => {
			clearInterval(interval);
			if (replyTimer !== null) clearTimeout(replyTimer);
			room.off(RoomEvent.DataReceived, onData);
			room.off(RoomEvent.ParticipantDisconnected, onLeft);
			end();
		};
	}, [source, room]);
}
